import {
  makeStyles,
  Grid,
  Typography,
  Card,
  Chip,
  IconButton,
  Box
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { ErrorBoundary } from "@common/error-boundary";
import { useGetKitQuery, useUpdateBookingKitTagsMutation, GetKit } from "@graphql";
import { BookingKitDocPreview } from "./booking-kit-doc-preview";
import React, { useEffect, useState } from "react";
import { ErrorCard, SpinnerOverlay } from "@common";
import { getReceiptPreviewUri } from "@bookings/BookingProfile/TimeReport/receipt-uri";
import { BookingKitActions } from "./booking-kit-actions";
import { BookingKitCard } from "./booking-kit-card";
import { BookingKitStatus } from "./booking-kit-status";
import { BookingSupportTagForm } from "@bookings/BookingProfile/BookingSupportTag/BookingSupportTagForm";
import { BookingKitEventLog } from "../booking-kit-event-log/booking-kit-event-log";
import locale from "@bonliva-crm/locale";
import { useGlobalStore } from "@hooks";
import { useLegacyNavigation } from "@navigation";
import moment from "moment";

type Props = {
  kitId: string;
};

export type DocItem = {
  id: string;
  link: string;
  title: string;
  documentType: string;
};

export const BookingKitProfileLayout = ({ kitId }: Props) => {
  const classes = useStyles();
  const { navigation } = useGlobalStore();
  const [activeDocLink, setActiveDocLink] = useState(null);
  const [docs, setDocs] = useState<DocItem[]>([]);

  const legacyNavigation = useLegacyNavigation();

  const { data, loading, error } = useGetKitQuery({
    variables: { id: kitId }
  });

  const [updateTags, { loading: loadingTags }] = useUpdateBookingKitTagsMutation();

  const handleSubmitTag = async (bookingId: string, comment: string, tags: string[]) => {
    const res = await updateTags({
      variables: {
        id: kitId,
        comment,
        tags
      },
      refetchQueries: [GetKit.OperationName]
    });

    return res.data.updateBookingKitTags;
  };

  useEffect(() => {
    if (data?.bookingKit?.documents?.length || data?.bookingKit?.receipts?.length) {
      const _docs = getListItems([
        ...(data?.bookingKit?.documents || []),
        ...(data?.bookingKit?.receipts || [])
      ]);
      setDocs(_docs);
      setActiveDocLink(_docs[0].link);
    }
  }, [data]);

  if (error) {
    return (
      <Box justifyContent="center" alignItems="center" display="flex" height="100%">
        <ErrorCard title="Cannot load Booking Kit" message={error.message} />
      </Box>
    );
  }

  if (loading || !data) {
    return <SpinnerOverlay show />;
  }

  const kit = data.bookingKit;

  return (
    <ErrorBoundary>
      {kit.deleted && (
        <Box className={classes.alertError}>
          {`${locale.Deleted} - ${kit.deletedByName} ${moment(kit.deletedOn).format(
            "YYYY-MM-DD HH:mm"
          )}`}
        </Box>
      )}
      <Grid container spacing={0} className={classes.root}>
        <Grid item xs={12}>
          <div className={classes.actionWrap}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <IconButton
                onClick={() => navigation.close(legacyNavigation.name)}
                aria-label="Close"
              >
                <Close />
              </IconButton>
              <Typography variant="h5" style={{ fontWeight: "bold" }}>
                {"Underlag"} {kit?.name}{" "}
              </Typography>
              <Chip label={locale.bookingKitType[kit?.type]} />
            </div>
            <BookingKitActions kit={kit} />
          </div>
        </Grid>
        <ErrorBoundary>
          <Grid item xs={12} container spacing={0} className={classes.cardWrap}>
            <Grid item xs={6}>
              <BookingKitCard
                loading={loading}
                data={data?.bookingKit}
                bookingId={data?.bookingKit.bookingId}
              />
            </Grid>
            {!loading && (
              <Grid item xs={6}>
                <Card className={classes.tagWrap}>
                  <BookingSupportTagForm
                    key={
                      kit?.bookingSupportTagStatus
                        ? kit?.bookingSupportTagStatus?.map((x) => x.tag).join()
                        : "[]"
                    } // hack to upd data on tag change
                    initialData={{
                      _id: kitId,
                      bookingSupportTagStatus: kit?.bookingSupportTagStatus,
                      bookingSupportTagComment: kit?.bookingSupportTagComment
                    }}
                    bookingOrKit={"bookingKit"}
                    onSubmit={handleSubmitTag}
                    loading={loadingTags}
                  />
                </Card>
              </Grid>
            )}
          </Grid>
        </ErrorBoundary>

        <Grid item xs={9}>
          <BookingKitDocPreview
            activeDocLink={activeDocLink ?? ""}
            docs={docs}
            setActiveDocLink={(url) => setActiveDocLink(url)}
          />
          <BookingKitEventLog kitId={kitId} />
        </Grid>
        <Grid item xs={3}>
          <Typography variant="h5" style={{ fontWeight: "bold", padding: 8 }}>
            {"Tidslinje"}
          </Typography>
          <BookingKitStatus
            statuses={kit?.statuses}
            checkWasValid={kit?.bookingSupportCheckWasValid}
          />
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};

function getListItems(docs: any[]): DocItem[] {
  const docItems = docs?.map((x) => ({
    id: x._id || x.uri,
    link: x.pdfUrl ? `${x.pdfUrl}?mode=view` : getReceiptPreviewUri(x.uri),
    title: x.name || x.title,
    documentType: x.documentType
  }));

  // Sort documents in the specified order: TimeReport first, then InvoiceForm, PayForm, SubcontractorPayForm, then others
  return docItems?.sort((a, b) => {
    const getSortOrder = (docType: string) => {
      if (docType === "CertifiedTimeReport") {
        return 0;
      }
      if (docType === "TimeReport") {
        return 1;
      }
      if (docType === "InvoiceForm") {
        return 2;
      }
      if (docType === "PayForm") {
        return 3;
      }
      if (docType === "SickPayForm") {
        return 4;
      }
      if (docType === "SubcontractorPayForm") {
        return 5;
      }
      return 6;
    };

    const orderA = getSortOrder(a.documentType);
    const orderB = getSortOrder(b.documentType);

    return orderA - orderB;
  });
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  cardWrap: {
    "& *": {
      boxShadow: "none !important"
    },
    backgroundColor: theme.palette.background.paper,
    margin: theme.spacing(1, 0)
  },
  tabs: {
    "& div > *": {
      boxShadow: "none"
    }
  },
  actionWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  alertInfo: {
    "&& > div:last-child": {
      flex: 1
    }
  },
  doubleCheckAtCreationTime: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap"
  },
  tagWrap: {
    padding: theme.spacing(2)
  },
  alertError: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(1)
  }
}));
